import { useState, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { 
  getUserProfile, 
  updateUserProfile, 
  updateUserProfileImage, 
  changePassword,
  enable2FA,
  disable2FA,
  verify2FAOTP,
  resend2FAOTP,
  verify2FASecurityQuestion,
  get2FASecurityQuestions,
  request2FAOTP
} from '../../store/slices/authSlice';

/**
 * Custom hook for JetSetter profile management using Redux
 * @returns {Object} Profile state and methods
 */
const useProfile = () => {
  const dispatch = useAppDispatch();
  const { user, profileLoading, passwordChangeLoading } = useAppSelector((state) => state.auth);
  
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [error, setError] = useState(null);

  const loadProfile = useCallback(async () => {
    try {
      setError(null);
      await dispatch(getUserProfile()).unwrap();
    } catch (err) {
      console.error('Failed to load profile:', err);
      setError(err);
    }
  }, [dispatch]);

  const updateProfile = useCallback(async (profileData) => {
    try {
      setError(null);
      const updatedUser = await dispatch(updateUserProfile(profileData)).unwrap();
      return updatedUser;
    } catch (err) {
      console.error('Failed to update profile:', err);
      setError(err);
      throw err;
    }
  }, [dispatch]);

  const uploadAvatar = useCallback(async (avatarFile) => {
    try {
      setError(null);
      
      // Convert file to base64 and upload
      const reader = new FileReader();
      
      return new Promise((resolve, reject) => {
        reader.onload = async (e) => {
          try {
            const base64Image = e.target.result;
            const result = await dispatch(updateUserProfileImage(base64Image)).unwrap();
            resolve({ avatarUrl: result.image_url });
          } catch (err) {
            reject(err);
          }
        };
        
        reader.onerror = (error) => {
          reject(error);
        };
        
        reader.readAsDataURL(avatarFile);
      });
    } catch (err) {
      console.error('Failed to upload avatar:', err);
      setError(err);
      throw err;
    }
  }, [dispatch]);

  const changeUserPassword = useCallback(async (passwordData) => {
    try {
      setError(null);
      await dispatch(changePassword({
        current_password: passwordData.currentPassword,
        password: passwordData.newPassword
      })).unwrap();
      
      return { success: true, message: 'Password changed successfully' };
    } catch (err) {
      console.error('Failed to change password:', err);
      setError(err);
      throw err;
    }
  }, [dispatch]);

  const enableTwoFactor = useCallback(async (twoFactorData) => {
    try {
      setError(null);
      
      // twoFactorData should contain: { method, security_questions? }
      const result = await dispatch(enable2FA(twoFactorData)).unwrap();
      setTwoFactorEnabled(true);
      
      return { 
        success: true, 
        message: 'Two-factor authentication enabled successfully',
        data: result
      };
    } catch (err) {
      console.error('Failed to enable two-factor auth:', err);
      setError(err);
      throw err;
    }
  }, [dispatch]);

  const disableTwoFactor = useCallback(async () => {
    try {
      setError(null);
      
      await dispatch(disable2FA()).unwrap();
      setTwoFactorEnabled(false);
      
      return { 
        success: true, 
        message: 'Two-factor authentication disabled successfully'
      };
    } catch (err) {
      console.error('Failed to disable two-factor auth:', err);
      setError(err);
      throw err;
    }
  }, [dispatch]);

  const verifyTwoFactor = useCallback(async (verificationData) => {
    try {
      setError(null);
      
      // verificationData can be: { email, otp } or { email, qId, answer }
      if (verificationData.otp) {
        // OTP verification
        const result = await dispatch(verify2FAOTP(verificationData)).unwrap();
        return { success: true, message: 'Two-factor code verified', data: result };
      } else if (verificationData.qId && verificationData.answer) {
        // Security question verification
        const result = await dispatch(verify2FASecurityQuestion(verificationData)).unwrap();
        return { success: true, message: 'Security question verified', data: result };
      } else {
        throw new Error('Invalid verification data');
      }
    } catch (err) {
      console.error('Failed to verify two-factor code:', err);
      setError(err);
      throw err;
    }
  }, [dispatch]);

  const requestTwoFactorOTP = useCallback(async (email, method = 'email') => {
    try {
      setError(null);
      
      const result = await dispatch(request2FAOTP({ email, method })).unwrap();
      return { success: true, message: `OTP sent via ${method}`, data: result };
    } catch (err) {
      console.error('Failed to request 2FA OTP:', err);
      setError(err);
      throw err;
    }
  }, [dispatch]);

  const resendTwoFactorOTP = useCallback(async (email, method = 'email') => {
    try {
      setError(null);
      
      const result = await dispatch(resend2FAOTP({ email, method })).unwrap();
      return { success: true, message: `OTP resent via ${method}`, data: result };
    } catch (err) {
      console.error('Failed to resend 2FA OTP:', err);
      setError(err);
      throw err;
    }
  }, [dispatch]);

  const fetchSecurityQuestions = useCallback(async (email) => {
    try {
      setError(null);
      
      const result = await dispatch(get2FASecurityQuestions({ email })).unwrap();
      return { success: true, data: result };
    } catch (err) {
      console.error('Failed to fetch security questions:', err);
      setError(err);
      throw err;
    }
  }, [dispatch]);

  return {
    profile: user,
    twoFactorEnabled: !!(user?.Twofa || user?.twofa),
    loading: profileLoading,
    passwordLoading: passwordChangeLoading,
    error,
    loadProfile,
    updateProfile,
    uploadAvatar,
    useProfile,
    changePassword: changeUserPassword,
    enableTwoFactor,
    disableTwoFactor,
    verifyTwoFactor,
    requestTwoFactorOTP,
    resendTwoFactorOTP,
    fetchSecurityQuestions
  };
};

export default useProfile;